// Three-quarter car views (default with the HD art; ?carview=0 turns them off).
// The existing top-down carCanvas is rotated to one of 32 headings and foreshortened (TILT). Its tilted silhouette becomes
// the footprint of a stack: a body tier (walls painted per pixel from where that pixel sits on the car: side or end, which
// axle, door seam, lamp, grille ...), the top-down art as the body's top plate, then a narrower cabin tier (glass walls)
// with the cabin part of the art on its roof. Walls are lit from one side. Baked lazily per (vehicle key, heading).
import { Texture } from 'pixi.js';
import { VEHICLES, type VehicleType } from '../../common/src/data';
import { PAINTS, canvas, tex } from './art';
import { K, dk, lt, mix } from './hd-kit';
import { carCanvas, outline } from './hd-sprites';

export const HEADINGS = 32;
/** vertical foreshortening of the top-down art (camera tilt) */
export const TILT = 0.8;
/** On by default with the HD art; `?carview=0` brings back the old rotated top-down cars for comparison. */
export const carViewOn = typeof location === 'undefined' || new URLSearchParams(location.search).get('carview') !== '0';
export const headingIndex = (a: number) => ((Math.round(a / (Math.PI * 2) * HEADINGS) % HEADINGS) + HEADINGS) % HEADINGS;

export interface CarView { tex: Texture; anchorY: number; w: number; h: number }
const cache = new Map<string, CarView>();
export const bakeStats = { count: 0, ms: 0 };

interface Geo { axles: number[]; axW: number; cab?: [number, number, number]; seams: number[]; bedEnd: number }
/** Positions in car-local x measured from the rear bumper (same numbers carCanvas paints with, minus its 4 px margin). */
function geoOf(t: VehicleType, L: number): Geo {
  if (t === 'bike') return { axles: [0, L - 6], axW: 6, seams: [], bedEnd: 0 };
  if (t === 'armored') return { axles: [5, 23, L - 12], axW: 7, cab: [12, 41, 5], seams: [], bedEnd: 0 };
  if (t === 'kei') return { axles: [5, L - 11], axW: 7, cab: [20, 33, 2], seams: [24], bedEnd: 20 };
  const sports = t === 'sports', rx = Math.floor(L * (sports ? .28 : .3)), rw = Math.floor(L * (sports ? .3 : .34));
  return { axles: [5, L - 11], axW: 7, cab: [rx - 5, rx + rw + 7, 3], seams: [rx + Math.floor(rw * .48), rx + rw + 6, rx - 2], bedEnd: 0 };
}
function bodyOf(t: VehicleType, paint: number, wrecked: boolean) {
  let b = PAINTS[((paint % PAINTS.length) + PAINTS.length) % PAINTS.length];
  if (t === 'police') b = K.white;
  if (t === 'armored') b = dk(K.slate, .05);
  if (t === 'autocab') b = lt(K.cyan, .72);
  if (wrecked) b = dk(K.slate, .52);
  return b;
}

/** The stacked canvas for one heading; the ground footprint centre is at (width/2, anchorY). */
export function carViewCanvas(t: VehicleType, paint: number, wrecked: boolean, gunner: boolean, deco: number, idx: number): { c: HTMLCanvasElement; anchorY: number } {
  const spec = VEHICLES[t], L = spec.len * 2, W = spec.wid * 2, H = spec.viewH, g0 = geoOf(t, L);
  const body = bodyOf(t, paint, wrecked), theta = idx * Math.PI * 2 / HEADINGS, ct = Math.cos(theta), st = Math.sin(theta);
  const cabH = g0.cab ? Math.max(2, Math.round(H * .4)) : 0, Hb = H - cabH, wheelH = Math.max(2, Math.round(Hb * .55));
  const src = carCanvas(t, paint, wrecked, gunner, deco);
  const D = Math.ceil(Math.hypot(src.width, src.height)) + 2, fH = Math.ceil(D * TILT);
  const [rot, rg] = canvas(D, D); rg.imageSmoothingEnabled = false;
  rg.translate(D / 2, D / 2); rg.rotate(theta); rg.drawImage(src, -src.width / 2, -src.height / 2);
  const [tl, tg] = canvas(D, fH); tg.imageSmoothingEnabled = false; tg.drawImage(rot, 0, 0, D, D, 0, 0, D, fH);
  const img = tg.getImageData(0, 0, D, fH), px = img.data, N = D * fH;
  // per-pixel classification of the tilted footprint from the inverse rotation
  const mask = new Uint8Array(N), cab = new Uint8Array(N), end = new Uint8Array(N), sgn = new Int8Array(N), lvl = new Uint8Array(N);
  const XO = new Float32Array(N), LY = new Float32Array(N);
  for (let y = 0; y < fH; y++) for (let x = 0; x < D; x++) {
    const i = y * D + x; if (px[i * 4 + 3] < 128) continue; mask[i] = 1;
    const dx = x + .5 - D / 2, dy = (y + .5 - fH / 2) / TILT, lx = dx * ct + dy * st, ly = -dx * st + dy * ct;
    const xo = lx + L / 2; XO[i] = xo; LY[i] = ly;
    const isEnd = L / 2 - Math.abs(lx) < W / 2 - Math.abs(ly); end[i] = isEnd ? 1 : 0;
    const s = isEnd ? Math.sign(lx) : Math.sign(ly); sgn[i] = s;
    const nx = isEnd ? s : 0, ny = isEnd ? 0 : s, wx = nx * ct - ny * st, wy = nx * st + ny * ct;
    const dot = (wx * .45 + wy * .9) / 1.006; // light from the top left: south and east faces catch more
    lvl[i] = dot > .55 ? 0 : dot > .1 ? 1 : 2;
    if (g0.cab && xo >= g0.cab[0] && xo <= g0.cab[1] && Math.abs(ly) <= W / 2 - g0.cab[2]) cab[i] = 1;
  }
  // distance to the next footprint pixel straight below (a wall pixel is visible only if no taller slice covers it)
  const below = (m: Uint8Array) => { const nb = new Uint8Array(N).fill(99); for (let x = 0; x < D; x++) { let d = 99; for (let y = fH - 1; y >= 0; y--) { nb[y * D + x] = d; d = m[y * D + x] ? 1 : Math.min(99, d + 1); } } return nb; };
  const nbBody = below(mask), nbCab = below(cab);
  const shMemo = new Map<string, string>();
  const sh = (c: string, l: number) => { const k = c + l; let v = shMemo.get(k); if (!v) { v = mix(c, K.ink, [.18, .36, .54][l]); shMemo.set(k, v); } return v; };
  const bodyWall = (k: number, i: number): string | null => {
    const xo = XO[i], ly = LY[i], l = lvl[i], isEnd = end[i] === 1, front = sgn[i] > 0, hw = W / 2;
    const ax = g0.axles.find(a => xo >= a && xo < a + g0.axW);
    if (ax !== undefined) {
      if (k < wheelH || (t === 'bike' && k < Hb)) {
        const mid = ax + g0.axW / 2, d = Math.abs(xo - mid);
        if (k === 1 && d < 1.2) return sh(K.metalHi, l);
        if (k === 1 && d < 2.4) return sh(K.metalLo, l);
        if (k === 2 && d < 1.2) return sh(K.metalLo, l);
        return Math.round(xo) % 2 ? K.ink : sh(K.tarHi, 2);
      }
    }
    if (k === wheelH && g0.axles.some(a => xo >= a - 1 && xo < a + g0.axW + 1)) return K.ink; // wheel arch
    if (t === 'bike') { if (k === 0) return null; return k >= Hb - 1 && xo > 7 && xo < 24 ? sh(body, l) : sh(k < 2 ? K.metalLo : K.metal, l); }
    if (k === 0) return K.ink;
    if (k === 1) return sh(isEnd ? K.metalHi : K.metalLo, l); // sill / bumper
    if (k === Hb - 1) return sh(lt(body, .5), l); // beltline highlight
    if (isEnd) {
      const aly = Math.abs(ly);
      if (t === 'armored' && front && aly < hw - 3) return ((Math.round(ly) + k) >> 1) & 1 ? (wrecked ? K.metalLo : K.amber) : K.ink;
      if (aly > hw - 6 && aly < hw - 2) return front ? (wrecked ? K.metalLo : k === 2 ? K.warm : K.warmHi) : (wrecked ? K.metalLo : k === 2 ? K.pink : K.red); // lamps
      if (front && aly < hw - 7) return Math.round(ly) & 1 ? K.metalLo : K.ink; // grille
      if (!front && aly < 2 && k < 4) return K.white; // plate
      return sh(body, l);
    }
    if (g0.seams.some(s => Math.abs(xo - s) < .5)) return sh(mix(body, K.ink, .8), 0); // door seam
    if (t === 'police' && k === 2 && !wrecked) return xo > L / 2 ? K.cyan : K.pink;
    if (t === 'autocab' && k === 2 && !wrecked) return (Math.floor(xo) >> 1) & 1 ? K.ink : K.white;
    if (g0.bedEnd && xo < g0.bedEnd) return Math.floor(xo) % 3 === 0 ? sh(K.metalHi, l) : sh(K.metalLo, l); // ribbed bed
    if (t === 'armored') return k === 2 && Math.floor(xo) % 8 === 1 ? K.white : sh(k & 1 ? body : dk(body, .3), l);
    return sh(body, l);
  };
  const cabWall = (k: number, i: number): string => {
    const xo = XO[i], l = lvl[i], c = g0.cab!;
    if (k === Hb) return sh(body, l);
    if (k === H - 1) return sh(lt(body, .3), l);
    if (t === 'armored') return k === H - 2 && Math.floor(xo) % 6 < 4 ? (Math.floor(xo) % 3 === 0 ? K.glassHi : K.ink) : sh(dk(body, .3), l);
    if (!end[i] && (xo < c[0] + 1.5 || xo > c[1] - 1.5 || g0.seams.some(s => Math.abs(xo - s) < .5))) return K.ink; // pillars
    return k === H - 2 ? K.glassHi : sh(K.glass, Math.min(l, 1));
  };
  const [out, g] = canvas(D, fH + H); g.imageSmoothingEnabled = false;
  const dot = (x: number, y: number, c: string) => { g.fillStyle = c; g.fillRect(x, y, 1, 1); };
  for (let k = 0; k < Hb; k++) for (let y = 0; y < fH; y++) for (let x = 0; x < D; x++) {
    const i = y * D + x; if (!mask[i] || nbBody[i] <= Hb - k) continue;
    const c = bodyWall(k, i); if (c) dot(x, y + H - k, c);
  }
  g.drawImage(tl, 0, H - Hb); // top-down art as the body's top plate
  if (g0.cab) {
    for (let k = Hb; k < H; k++) for (let y = 0; y < fH; y++) for (let x = 0; x < D; x++) {
      const i = y * D + x; if (!cab[i] || nbCab[i] <= H - k) continue;
      dot(x, y + H - k, cabWall(k, i));
    }
    const roof = tg.getImageData(0, 0, D, fH); for (let i = 0; i < N; i++) if (!cab[i]) roof.data[i * 4 + 3] = 0;
    const [rc, rcg] = canvas(D, fH); rcg.putImageData(roof, 0, 0); g.drawImage(rc, 0, 0); // cabin roof art
  }
  return { c: outline(out), anchorY: fH / 2 + H };
}

/** Texture for a vehicle at heading `a` (radians), picking the nearest heading. */
export function carViewTex(t: VehicleType, paint: number, wrecked: boolean, gunner: boolean, deco: number, a: number): CarView {
  const pc = ((paint % PAINTS.length) + PAINTS.length) % PAINTS.length, d = t === 'sports' ? deco % 3 : 0;
  const idx = headingIndex(a), key = `${t}:${pc}:${wrecked}:${gunner && t === 'kei'}:${d}:${idx}`;
  const hit = cache.get(key); if (hit) return hit;
  const t0 = performance.now();
  const { c, anchorY } = carViewCanvas(t, pc, wrecked, gunner, d, idx);
  const v = { tex: tex(c), anchorY: anchorY / c.height, w: c.width, h: c.height };
  bakeStats.count++; bakeStats.ms += performance.now() - t0;
  cache.set(key, v); return v;
}
